import { ActionIcon, Avatar, Box, Checkbox, FileButton, Group, Stack } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { I_IconSetting, useWidgetFormContext } from '@/components/Widget/Embed/form-context';
import { useWidgetForm } from '@/hooks/useWidgetForm';

import classes from './chatbotAvatar.module.css';
import {
  SvgAddPicture,
  SvgChatbotAvatar_1,
  SvgChatbotAvatar_2,
  SvgChatbotAvatar_3,
  SvgChatbotAvatar_4,
  SvgChatbotAvatar_5,
  SvgChatbotAvatar_6,
} from './icon';
import { useCanAccess } from '@mygpt/app';

import { SettingCard } from './SettingCard';

export const CHATBOT_AVATAR_ARRAY = [
  SvgChatbotAvatar_1,
  SvgChatbotAvatar_2,
  SvgChatbotAvatar_3,
  SvgChatbotAvatar_4,
  SvgChatbotAvatar_5,
  SvgChatbotAvatar_6,
];

/** 头像与显示：头像选择 + 回答来源 + Bot头像开关（共用） */
export default () => {
  const { t } = useTranslation();
  const { hovered, ref } = useHover();
  const { form } = useWidgetFormContext();
  const canAccessWidgetCustomization = useCanAccess('enable_widget_customization');
  const { onOverrideChangeForForm } = useWidgetForm();

  const [customFileSrc, setCustomFileSrc] = useState<string>('');
  const resetRef = useRef<() => void>(null);

  const changeFormChatBotIcon = (param: I_IconSetting) => {
    form.setFieldValue('chatbotAvatar', { ...param });

    // 模仿触发了 blur 时的保存效果，触发自动保存逻辑
    onOverrideChangeForForm('')({ type: 'blur', isTrusted: true });
  };

  const systemFile = (index: number) => {
    setCustomFileSrc('');
    resetRef.current?.();

    changeFormChatBotIcon({
      type: 'system',
      index: index,
      url: '',
    });
  };

  const customFile = (file: File | null) => {
    if (!file) return;
    if (file.size >= 2 * 1024 * 1024) {
      showNotification({
        message: t('embeddings.upload.max_file_size', { size: '2MB' }),
        color: 'red',
      });
      resetRef.current?.();
      return;
    }

    const reads = new FileReader();
    reads.readAsDataURL(file);
    reads.onload = function () {
      const src = this.result as string;
      setCustomFileSrc(src);

      changeFormChatBotIcon({
        type: 'custom',
        url: src,
        file: file,
      });
    };
  };

  const getCustomPreview = () => {
    if (customFileSrc) {
      return <Avatar radius="xl" src={customFileSrc} />;
    }

    return <SvgAddPicture />;
  };

  useEffect(() => {
    setCustomFileSrc(form.values.chatbotAvatar.url ?? '');
  }, [form.values]);

  return (
    <SettingCard title={t('widget.setting.groups.card_avatar_display')}>
      <Box className={classes.chatbotWrap}>
        <Group justify="space-between">
          <Group>
            {CHATBOT_AVATAR_ARRAY.map((ChatbotAvatarItem, index) => (
              <ActionIcon
                key={index}
                variant={index === form.values.chatbotAvatar.index ? 'filled' : 'light'}
                size="xl"
                radius="xl"
                disabled={!canAccessWidgetCustomization}
                onClick={() => {
                  systemFile(index);
                }}
              >
                <ChatbotAvatarItem />
              </ActionIcon>
            ))}
          </Group>

          <Group>
            <section className={classes.boxVerticalLine} />
            <Box ref={ref}>
              <FileButton
                resetRef={resetRef}
                onChange={customFile}
                accept="image/png,image/jpeg"
                disabled={!canAccessWidgetCustomization}
              >
                {(props) => (
                  <ActionIcon
                    className={classes.hoverBtn}
                    variant="outline"
                    size="xl"
                    radius="xl"
                    {...props}
                    disabled={!canAccessWidgetCustomization}
                  >
                    {canAccessWidgetCustomization && hovered ? <IconPlus /> : getCustomPreview()}
                  </ActionIcon>
                )}
              </FileButton>
            </Box>
          </Group>
        </Group>
      </Box>
      <Stack mt={20} gap={16}>
        <Checkbox
          disabled={!canAccessWidgetCustomization}
          label={t('widget.setting.chatbot.show_bot_avatar')}
          checked={form.values.chatbot.showBotAvatar.checked}
          {...form.getInputProps('chatbot.showBotAvatar.checked')}
          onChange={onOverrideChangeForForm('chatbot.showBotAvatar.checked')}
        />
      </Stack>
    </SettingCard>
  );
};
